import React, { useState, useEffect } from "react";
import { Modal, Button, Table, Form, FloatingLabel } from "react-bootstrap";
import { getToken } from "../../utilities/authHelper";
import { fetchSubscriptions } from "../../utilities/AdminUtilities";
function SubscriptionDetails() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editable, setEditable] = useState(false);
  const [editedSubscription, setEditedSubscription] = useState({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isCreatingNewSubscription, setIsCreatingNewSubscription] =
    useState(false);

  useEffect(() => {
    fetchSubscriptions(setSubscriptions);
  }, []);

  const handleSubscriptionClick = (subscription) => {
    setSelectedSubscription(subscription);
    setEditedSubscription({ ...subscription });
    setShowModal(true);
  };

  const handleEditChange = (e) => {
    setEditedSubscription({
      ...editedSubscription,
      [e.target.name]: e.target.value,
    });
  };

  const handleSave = async () => {
    const apiUrl = process.env.REACT_APP_API_URL;
    try {
      const response = await fetch(
        `${apiUrl}/admin/create_or_update_subscription${
          isCreatingNewSubscription
            ? ""
            : `?subscription_id=${selectedSubscription.id}`
        }`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify(editedSubscription),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to save subscription");
      }

      setShowSuccessModal(true);
      handleCloseModal();
      fetchSubscriptions();
    } catch (error) {
      console.error("Error saving subscription:", error);
    }
  };

  const handleCreateNewSubscription = () => {
    setIsCreatingNewSubscription(true);
    setEditedSubscription({});
    setShowModal(true);
    setEditable(true);
  };

  const handleCloseModal = () => {
    setEditable(false);
    setShowModal(false);
    setIsCreatingNewSubscription(false);
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
  };

  return (
    <>
      <Table striped bordered hover>
        <thead>
          <tr>
            <th>Subscription Name</th>
            <th>Type</th>
            <th>Status</th>
            <th>Validity (Days)</th>
            <th>Allowed Members Count (-1 : unlimited)</th>
            <th>Allowed GPT Count (-1 : unlimited)</th>
            <th>Package</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {subscriptions.map((subscription) => (
            <tr key={subscription.id}>
              <td>{subscription.subscription_name}</td>
              <td>{subscription.subscription_type}</td>
              <td>{subscription.status}</td>
              <td>{subscription.validity}</td>
              <td>{subscription.allowed_member_count}</td>
              <td>{subscription.allowed_gpt_usage_count}</td>
              <td>{subscription.package_id}</td>
              <td>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleSubscriptionClick(subscription)}
                >
                  Edit
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header>
          <Modal.Title>
            {isCreatingNewSubscription
              ? "Create New Subscription"
              : selectedSubscription?.subscription_name}
          </Modal.Title>
          <i
            className="fas fa-edit ml-2"
            style={{ cursor: "pointer" }}
            onClick={() => setEditable(!editable)}
          ></i>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3" controlId="subscriptionName">
              <FloatingLabel
                controlId="subscriptionName"
                label="Subscription Name"
              >
                <Form.Control
                  type="text"
                  name="subscription_name"
                  value={editedSubscription.subscription_name || ""}
                  placeholder="Subscription Name"
                  onChange={handleEditChange}
                  disabled={!editable}
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group className="mb-3" controlId="subscriptionType">
              <FloatingLabel
                controlId="subscriptionType"
                label="Subscription Type"
              >
                <Form.Control
                  type="text"
                  name="subscription_type"
                  value={editedSubscription.subscription_type || ""}
                  placeholder="Subscription Type"
                  onChange={handleEditChange}
                  disabled={!editable}
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group className="mb-3" controlId="validity">
              <FloatingLabel controlId="validity" label="Validity">
                <Form.Control
                  type="number"
                  name="validity"
                  value={editedSubscription.validity || ""}
                  placeholder="Validity (in days)"
                  onChange={handleEditChange}
                  disabled={!editable}
                />
              </FloatingLabel>
            </Form.Group>
            <Form.Group className="mb-3" controlId="allowedMemberCount">
              <FloatingLabel
                controlId="allowedMemberCount"
                label="Allowed Member Count"
              >
                <Form.Control
                  type="number"
                  name="allowed_member_count"
                  value={editedSubscription.allowed_member_count || ""}
                  placeholder="Allowed Member Count"
                  onChange={handleEditChange}
                  disabled={!editable}
                />
              </FloatingLabel>
            </Form.Group>
            <Form.Group className="mb-3" controlId="allowedGptUsageCount">
              <FloatingLabel
                controlId="allowedGptUsageCount"
                label="Allowed Gpt Usage Count"
              >
                <Form.Control
                  type="number"
                  name="allowed_gpt_usage_count"
                  value={editedSubscription.allowed_gpt_usage_count || ""}
                  placeholder="Allowed GPT Usage Count"
                  onChange={handleEditChange}
                  disabled={!editable}
                />
              </FloatingLabel>
            </Form.Group>
            <Form.Group className="mb-3" controlId="countryCodeList">
              <FloatingLabel
                controlId="countryCodeList"
                label="Country Code List"
              >
                <Form.Control
                  type="text"
                  name="country_code_list"
                  value={editedSubscription.country_code_list?.join(", ") || ""}
                  placeholder="Country Codes (comma separated)"
                  onChange={(e) =>
                    handleEditChange({
                      target: {
                        name: "country_code_list",
                        value: e.target.value
                          .split(",")
                          .map((code) => code.trim()),
                      },
                    })
                  }
                  disabled={!editable}
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group className="mb-3" controlId="status">
              <FloatingLabel controlId="status" label="Status">
                <Form.Control
                  type="text"
                  name="status"
                  value={editedSubscription.status || ""}
                  placeholder="Status"
                  onChange={handleEditChange}
                  disabled={!editable}
                />
              </FloatingLabel>
            </Form.Group>
            <Form.Group className="mb-3" controlId="packageId">
              <FloatingLabel controlId="packageId" label="Package ID">
                <Form.Control
                  type="text"
                  name="package_id"
                  value={editedSubscription.package_id || ""}
                  placeholder="Package ID"
                  onChange={handleEditChange}
                  disabled={!editable}
                />
              </FloatingLabel>
            </Form.Group>
            <Form.Group className="mb-3" controlId="creationTimestamp">
              <FloatingLabel
                controlId="creationTimestamp"
                label="Creation Timestamp"
              >
                <Form.Control
                  type="text"
                  name="creation_timestamp"
                  value={editedSubscription.creation_timestamp || ""}
                  placeholder="Creation Timestamp"
                  disabled={true}
                />
              </FloatingLabel>
            </Form.Group>
            <Form.Group className="mb-3" controlId="updationTimestamp">
              <FloatingLabel
                controlId="updationTimestamp"
                label="Updation Timestamp"
              >
                <Form.Control
                  type="text"
                  name="updation_timestamp"
                  value={editedSubscription.updation_timestamp || ""}
                  placeholder="Updation Timestamp"
                  disabled={true}
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group className="mb-3" controlId="terminationTimestamp">
              <FloatingLabel
                controlId="terminationTimestamp"
                label="Termination Timestamp"
              >
                <Form.Control
                  type="text"
                  name="termination_timestamp"
                  value={editedSubscription.termination_timestamp || ""}
                  placeholder="Termination Timestamp"
                  onChange={handleEditChange}
                  disabled={true}
                />
              </FloatingLabel>
            </Form.Group>
            <Form.Group className="mb-3" controlId="createdBy">
              <FloatingLabel controlId="createdBy" label="Created By">
                <Form.Control
                  type="text"
                  name="created_by"
                  value={editedSubscription.created_by || ""}
                  placeholder="Created By"
                  disabled={true}
                />
              </FloatingLabel>
            </Form.Group>
            {/* <Form.Group className="mb-3" controlId="detailsObject">
                  <FloatingLabel controlId="detailsObject" label="Details Object (JSON)">
                    <Form.Control
                      as="textarea"
                      name="details_object"
                      value={JSON.stringify(editedSubscription.details_object || {}, null, 2)}
                      placeholder="Details Object"
                      onChange={(e) => handleEditChange({
                        target: {
                          name: 'details_object',
                          value: e.target.value ? JSON.parse(e.target.value) : {}
                        }
                      })}
                      disabled={!editable}
                    />
                  </FloatingLabel>
                </Form.Group> */}
          </Form>
        </Modal.Body>
        {editable && (
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Close
            </Button>
            <Button variant="primary" onClick={handleSave}>
              Save
            </Button>
          </Modal.Footer>
        )}
      </Modal>

      <Modal show={showSuccessModal} onHide={handleCloseSuccessModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Success</Modal.Title>
        </Modal.Header>
        <Modal.Body>Your changes have been saved successfully.</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseSuccessModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      <div className="p-3 d-flex justify-content-center align-items-center">
        <Button variant="primary" onClick={handleCreateNewSubscription}>
          Create New Subscription
        </Button>
      </div>
    </>
  );
}

export default SubscriptionDetails;
